/**
 * AdminProjectDetail Component
 * PROJECT CONSOLIDATION CONSOLE — full-screen admin overlay opened from the
 * projects table. Split-pane layout:
 *   LEFT  — Execution overview: file packages grouped by taxonomy step,
 *           plus payment release checks (transactions).
 *   RIGHT — Drawing breakdown grouped by revision.
 *
 * ValidKernel Defensive Build Standard applied:
 * - Every API field treated as possibly null/absent (normalized on receipt)
 * - All rendered values pass through safeText() / explicit fallbacks
 * - Array.isArray guards on every collection; no raw objects reach JSX
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { api, projectApi } from '../services/api';

// SDIO v2.2 submittal return dispositions (306 Returned Submittals)
const RETURN_STATUSES = ['APP', 'AAN', 'RR', 'REJ', 'NET'] as const;

interface RoundDocRecord {
  id: number;
  filename?: string;
  taxonomy_code?: string;
  csi_code?: string;
  revision?: string;
  page_count?: number;
  storage_class?: string;
}

interface SubmittalRoundRecord {
  id: number;
  round_number: number;
  revision_label?: string;
  status?: string;
  return_status?: string | null;
  return_document_id?: number | null;
  submitted_at?: string | null;
  returned_at?: string | null;
  closed_at?: string | null;
  notes?: string | null;
  documents?: RoundDocRecord[];
}

// ============================================================================
// Props
// ============================================================================

interface AdminProjectDetailProps {
  projectId: number;
  onClose: () => void;
}

// ============================================================================
// Normalized record types (every field guaranteed present after normalize)
// ============================================================================

interface ClientInfo {
  id: number;
  name: string;
  client_code: string;
}

/** AI signal extraction (Document.parsed_data.ai.signals) — every field optional. */
interface AiSignals {
  manufacturer: string | null;
  roof_system: string | null;
  membrane: string | null;
  insulation: string | null;
  wind_uplift: string | null;
  warranty: string | null;
  square_footage: string | null;
}

/** Per-document AI insight (Document.parsed_data.ai). */
interface DocAiInsight {
  specialist: string;
  summary: string;
  doc_type: string;
  taxonomy_suggestion: string | null;
  confidence: number | null;
  signals: AiSignals;
  red_flags: string[];
  analyzed_at: string;
  model: string;
}

/** Project-level AI synthesis (Project.extra_data.ai_brief, serialized as ai_brief). */
interface AiBrief {
  brief: string;
  red_flags: string[];
  doc_count: number | null;
  generated_at: string;
  model: string;
}

interface DocRecord {
  id: number;
  filename: string;
  original_filename: string;
  document_type: string;
  step_number: number | null;
  is_parsed: boolean;
  taxonomy_code: string;
  csi_code: string;
  revision: string;
  page_count: number | null;
  original_page_count: number | null;
  is_client_filtered: boolean;
  storage_class: string;
  file_size: number | null;
  uploaded_at: string;
  classification_method: string;
  classification_confidence: number | null;
  ai: DocAiInsight | null;
  /** Deterministic-parser output (Document.parsed_data.assembly) — shape unknown, rendered defensively. */
  assembly: Record<string, any> | null;
}

interface DrawingRecord {
  id: number;
  drawing_number: string;
  title: string;
  revision: string;
  status: string;
  drawing_type: string;
  sheet_number: string;
}

interface TransactionRecord {
  id: number;
  amount: number | null;
  deposit_amount: number | null;
  page_fee_amount: number | null;
  status: string;
  created_at: string;
}

interface ProjectDetail {
  id: number;
  project_number: string;
  name: string;
  status: string;
  priority: string;
  due_date: string;
  tier_selected: string;
  deposit_paid: boolean;
  deposit_amount: number | null;
  quoted_amount: number | null;
  client: ClientInfo | null;
  documents: DocRecord[];
  drawings: DrawingRecord[];
  transactions: TransactionRecord[];
  ai_brief: AiBrief | null;
}

// ============================================================================
// Defensive helpers
// ============================================================================

/** Never let a non-string reach JSX. */
const safeText = (val: any): string => {
  if (val === null || val === undefined) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  if (typeof val === 'object') {
    return val.name || val.label || val.title || val.message || JSON.stringify(val);
  }
  return String(val);
};

/** Coerce to a finite number or null. */
const safeNum = (val: any): number | null => {
  if (val === null || val === undefined || val === '') return null;
  const n = Number(val);
  return Number.isFinite(n) ? n : null;
};

const safeId = (val: any): number => {
  const n = safeNum(val);
  return n === null ? 0 : n;
};

const formatMoney = (val: number | null): string => {
  if (val === null) return '—';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
  } catch {
    return `$${val.toFixed(2)}`;
  }
};

const formatDate = (iso: string): string => {
  const raw = safeText(iso).trim();
  if (!raw) return '—';
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleDateString();
};

const formatBytes = (val: number | null): string => {
  if (val === null || val < 0) return '';
  if (val < 1024) return `${val} B`;
  if (val < 1024 * 1024) return `${(val / 1024).toFixed(1)} KB`;
  return `${(val / (1024 * 1024)).toFixed(1)} MB`;
};

const titleCase = (val: string): string =>
  safeText(val)
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

/** True only for a non-null, non-array object. */
const isPlainObject = (val: any): val is Record<string, any> =>
  val !== null && typeof val === 'object' && !Array.isArray(val);

/** Values considered "nothing to show" — skipped in key/value tables. */
const isEmptyValue = (val: any): boolean => {
  if (val === null || val === undefined) return true;
  if (typeof val === 'string') return val.trim() === '';
  if (Array.isArray(val)) return val.length === 0;
  if (typeof val === 'object') {
    try {
      return Object.keys(val).length === 0;
    } catch {
      return true;
    }
  }
  return false;
};

/** Leaf formatter: lists join with ', '; objects collapse to inline JSON; else safeText. */
const formatLeafValue = (val: any): string => {
  if (Array.isArray(val)) {
    return val.map((item: any) => formatLeafValue(item)).join(', ');
  }
  if (isPlainObject(val)) {
    try {
      return JSON.stringify(val);
    } catch {
      return safeText(val);
    }
  }
  return safeText(val);
};

// ----------------------------------------------------------------------------
// Normalizers
// ----------------------------------------------------------------------------

const normalizeClient = (raw: any): ClientInfo | null => {
  if (!raw || typeof raw !== 'object') return null;
  return {
    id: safeId(raw.id),
    name: safeText(raw.name),
    client_code: safeText(raw.client_code).toUpperCase(),
  };
};

/** Coerce to a trimmed non-empty string or null. */
const safeOptText = (val: any): string | null => {
  const t = safeText(val).trim();
  return t !== '' ? t : null;
};

/** Coerce to an array of trimmed non-empty strings (drops junk entries). */
const safeStringList = (val: any): string[] =>
  (Array.isArray(val) ? val : [])
    .map((item: any) => safeText(item).trim())
    .filter((item: string) => item !== '');

const normalizeAiSignals = (raw: any): AiSignals => {
  const s = raw && typeof raw === 'object' ? raw : {};
  return {
    manufacturer: safeOptText(s.manufacturer),
    roof_system: safeOptText(s.roof_system),
    membrane: safeOptText(s.membrane),
    insulation: safeOptText(s.insulation),
    wind_uplift: safeOptText(s.wind_uplift),
    warranty: safeOptText(s.warranty),
    square_footage: safeOptText(s.square_footage),
  };
};

const normalizeDocAi = (raw: any): DocAiInsight | null => {
  if (!raw || typeof raw !== 'object') return null;
  const conf = safeNum(raw.confidence);
  return {
    specialist: safeText(raw.specialist).trim(),
    summary: safeText(raw.summary).trim(),
    doc_type: safeText(raw.doc_type).trim(),
    taxonomy_suggestion: safeOptText(raw.taxonomy_suggestion),
    confidence: conf !== null ? Math.min(1, Math.max(0, conf)) : null,
    signals: normalizeAiSignals(raw.signals),
    red_flags: safeStringList(raw.red_flags),
    analyzed_at: safeText(raw.analyzed_at),
    model: safeText(raw.model),
  };
};

const normalizeAiBrief = (raw: any): AiBrief | null => {
  if (!raw || typeof raw !== 'object') return null;
  const brief = safeText(raw.brief);
  const redFlags = safeStringList(raw.red_flags);
  // An "ai_brief" with neither text nor flags carries no signal — treat as absent.
  if (brief.trim() === '' && redFlags.length === 0) return null;
  return {
    brief,
    red_flags: redFlags,
    doc_count: safeNum(raw.doc_count),
    generated_at: safeText(raw.generated_at),
    model: safeText(raw.model),
  };
};

/**
 * Deterministic-parser extraction (Document.parsed_data.assembly).
 * Shape is parser-defined and unknown here — keep only a non-empty plain
 * object; everything else is treated as absent. Rendering is fully defensive.
 */
const normalizeAssembly = (raw: any): Record<string, any> | null => {
  if (!isPlainObject(raw)) return null;
  try {
    return Object.keys(raw).length > 0 ? raw : null;
  } catch {
    return null;
  }
};

const normalizeDocument = (raw: any): DocRecord => {
  const d = raw && typeof raw === 'object' ? raw : {};
  const parsedData = d.parsed_data && typeof d.parsed_data === 'object' ? d.parsed_data : {};
  return {
    id: safeId(d.id),
    filename: safeText(d.filename),
    original_filename: safeText(d.original_filename),
    document_type: safeText(d.document_type),
    step_number: safeNum(d.step_number),
    is_parsed: Boolean(d.is_parsed),
    taxonomy_code: safeText(d.taxonomy_code).trim(),
    csi_code: safeText(d.csi_code).trim(),
    revision: safeText(d.revision).trim(),
    page_count: safeNum(d.page_count),
    original_page_count: safeNum(d.original_page_count),
    is_client_filtered: Boolean(d.is_client_filtered),
    storage_class: safeText(d.storage_class).toLowerCase().trim(),
    file_size: safeNum(d.file_size),
    uploaded_at: safeText(d.uploaded_at),
    classification_method: safeText(d.classification_method),
    classification_confidence: safeNum(d.classification_confidence),
    ai: normalizeDocAi(parsedData.ai),
    assembly: normalizeAssembly(parsedData.assembly),
  };
};

const normalizeDrawing = (raw: any): DrawingRecord => {
  const d = raw && typeof raw === 'object' ? raw : {};
  return {
    id: safeId(d.id),
    drawing_number: safeText(d.drawing_number),
    title: safeText(d.title),
    revision: safeText(d.revision).trim(),
    status: safeText(d.status),
    drawing_type: safeText(d.drawing_type),
    sheet_number: safeText(d.sheet_number),
  };
};

const normalizeTransaction = (raw: any): TransactionRecord => {
  const t = raw && typeof raw === 'object' ? raw : {};
  return {
    id: safeId(t.id),
    amount: safeNum(t.amount),
    deposit_amount: safeNum(t.deposit_amount),
    page_fee_amount: safeNum(t.page_fee_amount),
    status: safeText(t.status).toLowerCase(),
    created_at: safeText(t.created_at),
  };
};

const normalizeProject = (raw: any): ProjectDetail => {
  const p = raw && typeof raw === 'object' ? raw : {};
  return {
    id: safeId(p.id),
    project_number: safeText(p.project_number),
    name: safeText(p.name),
    status: safeText(p.status),
    priority: safeText(p.priority),
    due_date: safeText(p.due_date),
    tier_selected: safeText(p.tier_selected),
    deposit_paid: Boolean(p.deposit_paid),
    deposit_amount: safeNum(p.deposit_amount),
    quoted_amount: safeNum(p.quoted_amount),
    client: normalizeClient(p.client),
    documents: (Array.isArray(p.documents) ? p.documents : []).map(normalizeDocument),
    drawings: (Array.isArray(p.drawings) ? p.drawings : []).map(normalizeDrawing),
    transactions: (Array.isArray(p.transactions) ? p.transactions : []).map(normalizeTransaction),
    ai_brief: normalizeAiBrief(p.ai_brief),
  };
};

// ----------------------------------------------------------------------------
// Badge color helpers
// ----------------------------------------------------------------------------

const getStatusColor = (status: string): string => {
  switch (safeText(status).toLowerCase()) {
    case 'intake': return 'var(--secondary, #64748b)';
    case 'queued': return 'var(--info, #06b6d4)';
    case 'in_progress': return 'var(--primary, #2563eb)';
    case 'review': return 'var(--warning, #eab308)';
    case 'approved': return 'var(--success, #22c55e)';
    case 'completed': return 'var(--success, #22c55e)';
    case 'rejected': return 'var(--danger, #ef4444)';
    default: return 'var(--secondary, #64748b)';
  }
};

const getPriorityColor = (priority: string): string => {
  switch (safeText(priority).toLowerCase()) {
    case 'urgent': return 'var(--danger, #ef4444)';
    case 'high': return 'var(--warning, #eab308)';
    case 'normal': return 'var(--secondary, #64748b)';
    case 'low': return 'var(--success, #22c55e)';
    default: return 'var(--secondary, #64748b)';
  }
};

const getTransactionStatusColor = (status: string): string => {
  switch (safeText(status).toLowerCase()) {
    case 'completed':
    case 'succeeded':
    case 'paid':
      return 'var(--success, #22c55e)';
    case 'pending':
    case 'processing':
      return 'var(--warning, #eab308)';
    case 'failed':
    case 'cancelled':
    case 'canceled':
    case 'refunded':
      return 'var(--danger, #ef4444)';
    default:
      return 'var(--secondary, #64748b)';
  }
};

/** Compare revisions descending (newest/highest first); numeric-aware. */
const compareRevisionDesc = (a: string, b: string): number =>
  safeText(b).localeCompare(safeText(a), undefined, { numeric: true, sensitivity: 'base' });

const UNCLASSIFIED = 'Unclassified';
const UNREVISED = 'Unrevised';

// ----------------------------------------------------------------------------
// Assembly extraction (deterministic parser) display helpers
// ----------------------------------------------------------------------------

interface AssemblySection {
  /** Subsection heading ('' = untitled single-assembly result). */
  title: string;
  fields: Record<string, any>;
}

/**
 * Split a parsed_data.assembly result into displayable sections.
 * Multi-assembly results carry an 'assemblies' array (one subsection per
 * assembly, titled by assembly_roof_area); anything else renders as a single
 * untitled assembly. Junk entries are dropped, never thrown on.
 */
const getAssemblySections = (assembly: Record<string, any>): AssemblySection[] => {
  try {
    const rawList = assembly['assemblies'];
    if (Array.isArray(rawList)) {
      const sections = rawList
        .filter(isPlainObject)
        .map((entry: Record<string, any>, i: number): AssemblySection => ({
          title: safeText(entry.assembly_roof_area).trim() || `Assembly ${i + 1}`,
          fields: entry,
        }));
      if (sections.length > 0) return sections;
    }
    return [{ title: '', fields: assembly }];
  } catch {
    return [{ title: '', fields: assembly }];
  }
};

// ============================================================================
// Component
// ============================================================================

const AdminProjectDetail: React.FC<AdminProjectDetailProps> = ({ projectId, onClose }) => {
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [aiNotice, setAiNotice] = useState<{ kind: 'info' | 'error'; text: string } | null>(null);
  const refetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchProject = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await projectApi.get(projectId);
      if (response.success && response.data) {
        // Some endpoints wrap the record ({ project: {...} }); handle both.
        const raw = (response.data as any).project !== undefined
          ? (response.data as any).project
          : response.data;
        setProject(normalizeProject(raw));
      } else {
        console.error('Failed to fetch project:', response.error);
        setError(safeText(response.error) || 'Failed to load project');
        setProject(null);
      }
    } catch (err) {
      console.error('Failed to fetch project:', err);
      setError('Failed to load project');
      setProject(null);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  // Clear any pending post-analysis refetch on unmount
  useEffect(() => {
    return () => {
      if (refetchTimerRef.current !== null) clearTimeout(refetchTimerRef.current);
    };
  }, []);

  /**
   * Kick off backend AI analysis of every project document + the job brief.
   * 202 => transient notice + ONE scheduled refetch (~15s).
   * 409 => AI not configured (missing ANTHROPIC_API_KEY on the server).
   */
  const handleAnalyze = useCallback(async () => {
    if (analyzing) return;
    setAnalyzing(true);
    setAiNotice(null);
    try {
      const response = await api.post(`/api/projects/${projectId}/analyze`);
      if (response.success) {
        setAiNotice({ kind: 'info', text: 'Analysis running — refresh in a moment' });
        if (refetchTimerRef.current !== null) clearTimeout(refetchTimerRef.current);
        refetchTimerRef.current = setTimeout(() => {
          refetchTimerRef.current = null;
          setAiNotice(null);
          fetchProject();
        }, 15000);
      } else {
        const errText = safeText(response.error);
        if (errText.toLowerCase().includes('not configured')) {
          setAiNotice({ kind: 'error', text: 'AI analysis not configured (set ANTHROPIC_API_KEY)' });
        } else {
          setAiNotice({ kind: 'error', text: errText || 'Failed to start AI analysis' });
        }
      }
    } catch (err) {
      console.error('Failed to start AI analysis:', err);
      setAiNotice({ kind: 'error', text: 'Failed to start AI analysis' });
    } finally {
      setAnalyzing(false);
    }
  }, [analyzing, projectId, fetchProject]);

  // ==========================================================================
  // Submittal rounds (SDIO revision lifecycle)
  // ==========================================================================
  const [rounds, setRounds] = useState<SubmittalRoundRecord[]>([]);
  const [roundsError, setRoundsError] = useState('');
  const [roundsBusy, setRoundsBusy] = useState(false);

  const fetchRounds = useCallback(async () => {
    try {
      const response = await api.get(`/api/projects/${projectId}/rounds`);
      if (response.success && response.data) {
        const list = (response.data as any).rounds;
        setRounds(Array.isArray(list) ? list : []);
        setRoundsError('');
      } else {
        setRoundsError(safeText(response.error) || 'Failed to load rounds');
      }
    } catch (err) {
      console.error('Failed to fetch rounds:', err);
      setRoundsError('Failed to load submittal rounds');
    }
  }, [projectId]);

  useEffect(() => {
    fetchRounds();
  }, [fetchRounds]);

  const roundAction = useCallback(async (
    path: string,
    body?: Record<string, unknown>,
  ) => {
    if (roundsBusy) return;
    setRoundsBusy(true);
    setRoundsError('');
    try {
      const response = await api.post(path, body || {});
      if (!response.success) {
        setRoundsError(safeText(response.error) || 'Round action failed');
      }
      await fetchRounds();
      await fetchProject(); // round actions can flip project status
    } catch (err) {
      console.error('Round action failed:', err);
      setRoundsError('Round action failed');
    } finally {
      setRoundsBusy(false);
    }
  }, [roundsBusy, fetchRounds, fetchProject]);

  const hasActiveRound = rounds.some(
    (r) => r?.status === 'drafting' || r?.status === 'submitted',
  );

  // Escape key closes the console
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // --------------------------------------------------------------------------
  // Derived data (all defensive; project may be null)
  // --------------------------------------------------------------------------

  const documents = useMemo<DocRecord[]>(
    () => (project && Array.isArray(project.documents) ? project.documents : []),
    [project]
  );
  const drawings = useMemo<DrawingRecord[]>(
    () => (project && Array.isArray(project.drawings) ? project.drawings : []),
    [project]
  );
  const transactions = useMemo<TransactionRecord[]>(
    () => (project && Array.isArray(project.transactions) ? project.transactions : []),
    [project]
  );

  const totalPages = useMemo(
    () => documents.reduce((sum, d) => sum + (d.page_count !== null ? d.page_count : 0), 0),
    [documents]
  );

  const quarantineCount = useMemo(
    () => documents.filter((d) => d.storage_class === 'quarantine').length,
    [documents]
  );
  const hotCount = useMemo(
    () => documents.filter((d) => d.storage_class === 'hot').length,
    [documents]
  );

  /** Documents grouped by taxonomy_code; Unclassified last. */
  const docGroups = useMemo<Array<{ key: string; docs: DocRecord[] }>>(() => {
    const map = new Map<string, DocRecord[]>();
    documents.forEach((doc) => {
      const key = doc.taxonomy_code !== '' ? doc.taxonomy_code : UNCLASSIFIED;
      const bucket = map.get(key);
      if (bucket) {
        bucket.push(doc);
      } else {
        map.set(key, [doc]);
      }
    });
    const keys = Array.from(map.keys()).sort((a, b) => {
      if (a === UNCLASSIFIED) return 1;
      if (b === UNCLASSIFIED) return -1;
      return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    });
    return keys.map((key) => ({ key, docs: map.get(key) || [] }));
  }, [documents]);

  /** Drawings grouped by revision; newest/highest first, Unrevised last. */
  const drawingGroups = useMemo<Array<{ key: string; items: DrawingRecord[] }>>(() => {
    const map = new Map<string, DrawingRecord[]>();
    drawings.forEach((dw) => {
      const key = dw.revision !== '' ? dw.revision : UNREVISED;
      const bucket = map.get(key);
      if (bucket) {
        bucket.push(dw);
      } else {
        map.set(key, [dw]);
      }
    });
    const keys = Array.from(map.keys()).sort((a, b) => {
      if (a === UNREVISED) return 1;
      if (b === UNREVISED) return -1;
      return compareRevisionDesc(a, b);
    });
    return keys.map((key) => ({ key, items: map.get(key) || [] }));
  }, [drawings]);

  /** Transactions newest first (defensive re-sort; ISO strings compare lexically). */
  const sortedTransactions = useMemo<TransactionRecord[]>(
    () => [...transactions].sort((a, b) => safeText(b.created_at).localeCompare(safeText(a.created_at))),
    [transactions]
  );

  // --------------------------------------------------------------------------
  // Sub-renderers
  // --------------------------------------------------------------------------

  const renderStorageBadge = (storageClass: string) => {
    if (storageClass === 'quarantine') {
      return <span className="apd-badge apd-badge-quarantine">Held — awaiting payment</span>;
    }
    if (storageClass === 'hot') {
      return <span className="apd-badge apd-badge-hot">Released</span>;
    }
    if (storageClass === '') {
      return <span className="apd-badge apd-badge-neutral">Unknown storage</span>;
    }
    return <span className="apd-badge apd-badge-neutral">{titleCase(storageClass)}</span>;
  };

  /** Amber warning lines shared by doc insights and the job brief. */
  const renderRedFlags = (flags: string[]) => {
    if (!Array.isArray(flags) || flags.length === 0) return null;
    return (
      <div className="apd-ai-flags">
        {flags.map((flag, i) => (
          <div className="apd-ai-flag" key={`${i}-${flag}`}>⚠ {safeText(flag)}</div>
        ))}
      </div>
    );
  };

  /** AI insight block rendered under a document row (parsed_data.ai). */
  const renderDocAi = (ai: DocAiInsight) => {
    const signalChips: Array<{ label: string; value: string }> = [];
    if (ai.signals.manufacturer) signalChips.push({ label: 'Mfr', value: ai.signals.manufacturer });
    if (ai.signals.roof_system) signalChips.push({ label: 'System', value: ai.signals.roof_system });
    if (ai.signals.membrane) signalChips.push({ label: 'Membrane', value: ai.signals.membrane });
    if (ai.signals.wind_uplift) signalChips.push({ label: 'Uplift', value: ai.signals.wind_uplift });
    const confidencePct = ai.confidence !== null ? Math.round(ai.confidence * 100) : null;
    return (
      <div className="apd-ai-insight">
        {ai.summary !== '' && <div className="apd-ai-summary">{safeText(ai.summary)}</div>}
        {(signalChips.length > 0 || confidencePct !== null) && (
          <div className="apd-ai-chips">
            {signalChips.map((chip) => (
              <span className="apd-chip apd-chip-signal" key={chip.label} title={chip.label}>
                {chip.label}: {safeText(chip.value)}
              </span>
            ))}
            {confidencePct !== null && (
              <span
                className="apd-chip apd-chip-confidence"
                title={`AI confidence${ai.specialist !== '' ? ` · ${titleCase(ai.specialist)} specialist` : ''}${ai.model !== '' ? ` · ${safeText(ai.model)}` : ''}`}
              >
                {confidencePct}% conf
              </span>
            )}
          </div>
        )}
        {renderRedFlags(ai.red_flags)}
      </div>
    );
  };

  const renderDocRow = (doc: DocRecord) => {
    const displayName = doc.original_filename || doc.filename || '(unnamed file)';
    const tooltipParts: string[] = [];
    if (doc.filename && doc.filename !== displayName) tooltipParts.push(`Stored as: ${doc.filename}`);
    if (doc.uploaded_at) tooltipParts.push(`Uploaded: ${formatDate(doc.uploaded_at)}`);
    if (doc.classification_method) {
      const conf = doc.classification_confidence !== null
        ? ` (${Math.round(doc.classification_confidence * 100)}%)`
        : '';
      tooltipParts.push(`Classified via ${doc.classification_method}${conf}`);
    }
    const sizeLabel = formatBytes(doc.file_size);
    if (sizeLabel) tooltipParts.push(`Size: ${sizeLabel}`);

    return (
      <React.Fragment key={doc.id}>
        <div className="apd-doc-row" title={tooltipParts.join('\n') || undefined}>
          <div className="apd-doc-main">
            <span className="apd-doc-name">{safeText(displayName)}</span>
            <span className="apd-doc-meta">
              {doc.csi_code !== '' && <span className="apd-chip apd-chip-csi">{safeText(doc.csi_code)}</span>}
              {doc.revision !== '' && <span className="apd-rev-tag">Rev {safeText(doc.revision)}</span>}
              <span className="apd-pages">
                {doc.page_count !== null ? `${doc.page_count} pg` : '— pg'}
                {doc.is_client_filtered && doc.original_page_count !== null
                  ? ` of ${doc.original_page_count}`
                  : ''}
              </span>
              {doc.is_client_filtered && (
                <span className="apd-chip apd-chip-filtered" title="Client narrowed this document to relevant pages">
                  Client-filtered
                </span>
              )}
            </span>
          </div>
          <div className="apd-doc-flags">
            <span className={`apd-parsed ${doc.is_parsed ? 'is-parsed' : 'not-parsed'}`}>
              {doc.is_parsed ? '● Parsed' : '○ Not parsed'}
            </span>
            {renderStorageBadge(doc.storage_class)}
          </div>
        </div>
        {doc.ai !== null && renderDocAi(doc.ai)}
      </React.Fragment>
    );
  };

  const renderDocGroup = (group: { key: string; docs: DocRecord[] }) => {
    const first = group.docs.length > 0 ? group.docs[0] : null;
    const subtitleParts: string[] = [];
    if (first && first.step_number !== null) subtitleParts.push(`Step ${first.step_number}`);
    if (first && first.document_type !== '') subtitleParts.push(titleCase(first.document_type));
    return (
      <div className="apd-group" key={group.key}>
        <div className="apd-group-header">
          <span className="apd-group-code">{safeText(group.key)}</span>
          {subtitleParts.length > 0 && (
            <span className="apd-group-subtitle">{subtitleParts.join(' · ')}</span>
          )}
          <span className="apd-group-count">
            {group.docs.length} file{group.docs.length === 1 ? '' : 's'}
          </span>
        </div>
        <div className="apd-group-body">
          {group.docs.map(renderDocRow)}
        </div>
      </div>
    );
  };

  const renderTransactionRow = (txn: TransactionRecord) => {
    const color = getTransactionStatusColor(txn.status);
    const hasSplit = txn.deposit_amount !== null || txn.page_fee_amount !== null;
    return (
      <div className="apd-txn-row" key={txn.id}>
        <div className="apd-txn-main">
          <span className="apd-txn-amount">{formatMoney(txn.amount)}</span>
          {hasSplit && (
            <span className="apd-txn-split">
              {txn.deposit_amount !== null && `Deposit ${formatMoney(txn.deposit_amount)}`}
              {txn.deposit_amount !== null && txn.page_fee_amount !== null && ' + '}
              {txn.page_fee_amount !== null && `Page fee ${formatMoney(txn.page_fee_amount)}`}
            </span>
          )}
        </div>
        <div className="apd-txn-side">
          <span
            className="status-badge"
            style={{ backgroundColor: `${color}20`, color }}
          >
            {titleCase(txn.status) || 'Unknown'}
          </span>
          <span className="apd-txn-date">{formatDate(txn.created_at)}</span>
        </div>
      </div>
    );
  };

  const renderDrawingRow = (dw: DrawingRecord) => {
    const color = getStatusColor(dw.status);
    return (
      <div className="apd-drawing-row" key={dw.id}>
        <div className="apd-drawing-main">
          <span className="apd-drawing-number">{safeText(dw.drawing_number) || '—'}</span>
          <span className="apd-drawing-title">{safeText(dw.title) || '(untitled)'}</span>
        </div>
        <div className="apd-drawing-side">
          {dw.drawing_type !== '' && (
            <span className="apd-chip apd-chip-type">{titleCase(dw.drawing_type)}</span>
          )}
          {dw.sheet_number !== '' && (
            <span className="apd-sheet">Sht {safeText(dw.sheet_number)}</span>
          )}
          <span
            className="status-badge"
            style={{ backgroundColor: `${color}20`, color }}
          >
            {titleCase(dw.status) || 'Unknown'}
          </span>
        </div>
      </div>
    );
  };

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------

  const clientLabel = project && project.client
    ? `${safeText(project.client.name) || 'Unnamed client'}${project.client.client_code ? ` (${project.client.client_code})` : ''}`
    : 'No client linked';

  const statusColor = getStatusColor(project ? project.status : '');
  const priorityColor = getPriorityColor(project ? project.priority : '');

  return (
    <div className="apd-overlay" role="dialog" aria-modal="true" aria-label="Project consolidation console">
      {/* ================= HEADER BAR ================= */}
      <div className="apd-header">
        <div className="apd-header-left">
          <div className="apd-title-block">
            <span className="apd-project-number">
              {project ? safeText(project.project_number) || '—' : '…'}
            </span>
            <h2 className="apd-project-name">
              {project ? safeText(project.name) || '(unnamed project)' : 'Loading project…'}
            </h2>
          </div>
          <span className="apd-client-chip" title="Client">{clientLabel}</span>
          {project && project.status !== '' && (
            <span
              className="status-badge"
              style={{ backgroundColor: `${statusColor}20`, color: statusColor }}
            >
              {titleCase(project.status)}
            </span>
          )}
          {project && project.priority !== '' && (
            <span
              className="status-badge"
              style={{ backgroundColor: `${priorityColor}20`, color: priorityColor }}
            >
              {titleCase(project.priority)}
            </span>
          )}
          {project && project.tier_selected !== '' && (
            <span className="apd-chip apd-chip-tier" title="Selected pricing tier">
              {titleCase(project.tier_selected)} tier
            </span>
          )}
          <span className="apd-due" title="Due date">
            Due: {project ? formatDate(project.due_date) : '—'}
          </span>
        </div>
        <div className="apd-header-right">
          {aiNotice !== null && (
            <span
              className={`apd-ai-notice ${aiNotice.kind === 'error' ? 'apd-ai-notice-error' : ''}`}
              role="status"
            >
              {safeText(aiNotice.text)}
            </span>
          )}
          <button
            className="btn secondary apd-analyze-btn"
            onClick={handleAnalyze}
            disabled={analyzing}
            title="Run AI analysis across all project documents, then generate the job brief"
          >
            {analyzing ? 'Starting…' : 'Analyze with AI'}
          </button>
          <button className="close-btn apd-close" onClick={onClose} aria-label="Close console">×</button>
        </div>
      </div>

      {/* ================= BODY ================= */}
      <div className="apd-body">
        {loading ? (
          <div className="apd-loading">Loading project console…</div>
        ) : error ? (
          <div className="apd-error">
            <div className="apd-error-message">{safeText(error)}</div>
            <div className="apd-error-actions">
              <button className="btn secondary" onClick={fetchProject}>Retry</button>
              <button className="btn" onClick={onClose}>Close</button>
            </div>
          </div>
        ) : !project ? (
          <div className="apd-error">
            <div className="apd-error-message">Project not found.</div>
            <div className="apd-error-actions">
              <button className="btn" onClick={onClose}>Close</button>
            </div>
          </div>
        ) : (
          <>
            {/* ================= STAT STRIP ================= */}
            <div className="stats-row apd-stats">
              <div className="stat-pill">
                <span className="stat-value">{documents.length}</span>
                <span className="stat-label">Documents</span>
              </div>
              <div className="stat-pill">
                <span className="stat-value">{totalPages}</span>
                <span className="stat-label">Total Pages</span>
              </div>
              <div className="stat-pill">
                <span className="stat-value">{drawings.length}</span>
                <span className="stat-label">Drawings</span>
              </div>
              <div className="stat-pill">
                <span className="stat-value">{formatMoney(project.quoted_amount)}</span>
                <span className="stat-label">Quoted</span>
              </div>
              <div className={`stat-pill ${project.deposit_paid ? 'apd-pill-paid' : 'apd-pill-unpaid'}`}>
                <span className="stat-value">{formatMoney(project.deposit_amount)}</span>
                <span className="stat-label">Deposit</span>
                <span className={`apd-badge ${project.deposit_paid ? 'apd-badge-hot' : 'apd-badge-quarantine'}`}>
                  {project.deposit_paid ? 'Paid' : 'Unpaid'}
                </span>
              </div>
            </div>

            {/* ================= SPLIT PANES ================= */}
            <div className="apd-panes">
              {/* -------- LEFT PANE: EXECUTION OVERVIEW -------- */}
              <section className="apd-pane">
                <h3 className="apd-pane-title">Execution Overview</h3>

                {/* AI Job Brief — Foreman-style synthesis of the whole job */}
                {project.ai_brief !== null && (
                  <div className="apd-ai-brief">
                    <div className="apd-ai-brief-title">AI Job Brief</div>
                    {project.ai_brief.brief.trim() !== '' && (
                      <div className="apd-ai-brief-text">{safeText(project.ai_brief.brief)}</div>
                    )}
                    {renderRedFlags(project.ai_brief.red_flags)}
                    <div className="apd-ai-brief-footer">
                      Generated {formatDate(project.ai_brief.generated_at)}
                      {project.ai_brief.model !== '' ? ` · ${safeText(project.ai_brief.model)}` : ''}
                    </div>
                  </div>
                )}

                {/* Release banner */}
                {documents.length > 0 && quarantineCount > 0 && (
                  <div className="apd-banner apd-banner-quarantine">
                    {quarantineCount} file{quarantineCount === 1 ? '' : 's'} in quarantine — released on deposit payment
                  </div>
                )}
                {documents.length > 0 && quarantineCount === 0 && hotCount === documents.length && (
                  <div className="apd-banner apd-banner-released">Files released</div>
                )}

                {/* File packages grouped by taxonomy step */}
                {docGroups.length === 0 ? (
                  <div className="apd-empty">No documents uploaded yet.</div>
                ) : (
                  docGroups.map(renderDocGroup)
                )}

                {/* Payment release checks */}
                <div className="apd-subsection">
                  <h4 className="apd-subsection-title">Payment Release Checks</h4>
                  {sortedTransactions.length === 0 ? (
                    <div className="apd-empty">No payment transactions recorded.</div>
                  ) : (
                    <div className="apd-txn-list">
                      {sortedTransactions.map(renderTransactionRow)}
                    </div>
                  )}
                </div>
              </section>

              {/* -------- RIGHT PANE: DRAWING BREAKDOWN -------- */}
              <section className="apd-pane">
                <h3 className="apd-pane-title">Drawing Breakdown</h3>
                {drawingGroups.length === 0 ? (
                  <div className="apd-empty">
                    No drawings produced yet — drawings appear here as drafting completes.
                  </div>
                ) : (
                  drawingGroups.map((group) => (
                    <div className="apd-group" key={group.key}>
                      <div className="apd-group-header">
                        <span className="apd-group-code">
                          {group.key === UNREVISED ? UNREVISED : `Rev ${safeText(group.key)}`}
                        </span>
                        <span className="apd-group-count">
                          {group.items.length} drawing{group.items.length === 1 ? '' : 's'}
                        </span>
                      </div>
                      <div className="apd-group-body">
                        {group.items.map(renderDrawingRow)}
                      </div>
                    </div>
                  ))
                )}
              </section>
            </div>

            <section className="apd-pane apd-rounds">
              <div className="apd-rounds-header">
                <h3 className="apd-pane-title">Submittal Rounds</h3>
                {!hasActiveRound && (
                  <button
                    className="apd-round-btn apd-round-btn-primary"
                    disabled={roundsBusy}
                    onClick={() => roundAction(`/api/projects/${projectId}/rounds`)}
                  >
                    Open {`R${String(rounds.length).padStart(2, '0')}`}
                  </button>
                )}
              </div>
              {roundsError !== '' && (
                <div className="apd-rounds-error">{safeText(roundsError)}</div>
              )}
              {rounds.length === 0 ? (
                <div className="apd-empty">
                  No submittal rounds yet — open R00 when the first set goes out.
                </div>
              ) : (
                <div className="apd-rounds-track">
                  {rounds.map((r) => {
                    const status = safeText(r?.status);
                    const docs = Array.isArray(r?.documents) ? r.documents : [];
                    return (
                      <div className={`apd-round-card apd-round-${status}`} key={safeText(r?.id)}>
                        <div className="apd-round-top">
                          <span className="apd-round-label">
                            {safeText(r?.revision_label) || `R${safeText(r?.round_number)}`}
                          </span>
                          <span className={`apd-round-status apd-round-status-${status}`}>
                            {status.toUpperCase()}
                            {r?.return_status ? ` — ${safeText(r.return_status)}` : ''}
                          </span>
                        </div>
                        <div className="apd-round-dates">
                          {r?.submitted_at ? `Submitted ${new Date(r.submitted_at).toLocaleDateString()}` : 'Not submitted'}
                          {r?.returned_at ? ` · Returned ${new Date(r.returned_at).toLocaleDateString()}` : ''}
                        </div>
                        {docs.length > 0 && (
                          <div className="apd-round-docs">
                            {docs.length} doc{docs.length === 1 ? '' : 's'}:{' '}
                            {docs.slice(0, 4).map((d) => safeText(d?.taxonomy_code)).filter(Boolean).join(', ')}
                            {docs.length > 4 ? '…' : ''}
                          </div>
                        )}
                        {safeText(r?.notes) !== '' && (
                          <div className="apd-round-notes">{safeText(r?.notes)}</div>
                        )}
                        {status === 'drafting' && (
                          <button
                            className="apd-round-btn apd-round-btn-primary"
                            disabled={roundsBusy}
                            onClick={() => roundAction(`/api/rounds/${r.id}/submit`)}
                          >
                            Mark Submitted
                          </button>
                        )}
                        {status === 'submitted' && (
                          <div className="apd-round-returns">
                            <span className="apd-round-returns-label">Record return:</span>
                            {RETURN_STATUSES.map((rs) => (
                              <button
                                key={rs}
                                className={`apd-round-btn apd-round-btn-${rs === 'RR' || rs === 'REJ' ? 'danger' : 'ok'}`}
                                disabled={roundsBusy}
                                title={rs === 'RR' || rs === 'REJ'
                                  ? `${rs}: closes this round and opens the next`
                                  : rs}
                                onClick={() => roundAction(`/api/rounds/${r.id}/return`, { return_status: rs })}
                              >
                                {rs}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </>
        )}
      </div>

      <style>{`
        .apd-rounds { margin-top: 16px; }
        .apd-rounds-header {
          display: flex; align-items: center; justify-content: space-between;
          gap: 12px; flex-wrap: wrap;
        }
        .apd-rounds-error {
          color: var(--red, #f87171); font-size: 0.8125rem; margin: 8px 0;
        }
        .apd-rounds-track {
          display: flex; gap: 12px; overflow-x: auto; padding: 8px 0;
        }
        .apd-round-card {
          min-width: 240px; max-width: 320px; flex: 0 0 auto;
          border: 1px solid var(--border-color, #334155);
          border-radius: 8px; padding: 12px;
          background: var(--bg-secondary, #1e293b);
        }
        .apd-round-card.apd-round-drafting { border-color: #3b82f6; }
        .apd-round-card.apd-round-submitted { border-color: #f59e0b; }
        .apd-round-card.apd-round-closed { opacity: 0.75; }
        .apd-round-top {
          display: flex; align-items: center; justify-content: space-between; gap: 8px;
        }
        .apd-round-label { font-weight: 700; font-size: 1rem; }
        .apd-round-status { font-size: 0.6875rem; letter-spacing: 0.04em; }
        .apd-round-status-drafting { color: #60a5fa; }
        .apd-round-status-submitted { color: #fbbf24; }
        .apd-round-status-returned { color: #34d399; }
        .apd-round-status-closed { color: var(--text-muted, #64748b); }
        .apd-round-dates, .apd-round-docs {
          font-size: 0.75rem; color: var(--text-secondary, #94a3b8); margin-top: 6px;
        }
        .apd-round-notes {
          font-size: 0.75rem; color: var(--text-muted, #64748b);
          margin-top: 6px; white-space: pre-wrap;
        }
        .apd-round-returns {
          display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin-top: 10px;
        }
        .apd-round-returns-label {
          font-size: 0.75rem; color: var(--text-secondary, #94a3b8);
        }
        .apd-round-btn {
          border: 1px solid var(--border-color, #334155); border-radius: 6px;
          background: var(--bg-primary, #0f172a); color: var(--text-primary, #e2e8f0);
          font-size: 0.75rem; padding: 4px 10px; cursor: pointer; margin-top: 8px;
        }
        .apd-round-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .apd-round-btn-primary { border-color: #3b82f6; color: #93c5fd; }
        .apd-round-btn-ok:hover { border-color: #34d399; }
        .apd-round-btn-danger { border-color: #7f1d1d; color: #fca5a5; }

        .apd-overlay {
          position: fixed;
          inset: 0;
          z-index: 1000;
          background: var(--bg-primary, #0f172a);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .apd-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 16px 24px;
          border-bottom: 1px solid var(--border-color, #334155);
          background: var(--bg-secondary, #1e293b);
          flex-wrap: wrap;
        }

        .apd-header-left {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
          min-width: 0;
        }

        .apd-title-block {
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0;
        }

        .apd-project-number {
          font-size: 12px;
          font-family: monospace;
          color: var(--text-secondary, #94a3b8);
          letter-spacing: 0.5px;
        }

        .apd-project-name {
          margin: 0;
          font-size: 18px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 40vw;
        }

        .apd-client-chip {
          padding: 4px 12px;
          border-radius: 14px;
          background: rgba(37, 99, 235, 0.18);
          color: var(--text-primary, #f1f5f9);
          font-size: 13px;
          white-space: nowrap;
        }

        .apd-due {
          font-size: 13px;
          color: var(--text-secondary, #94a3b8);
          white-space: nowrap;
        }

        .apd-header-right {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-shrink: 0;
        }

        .apd-ai-notice {
          font-size: 12px;
          color: var(--info, #06b6d4);
          white-space: nowrap;
        }

        .apd-ai-notice-error {
          color: var(--warning, #eab308);
        }

        .apd-analyze-btn {
          white-space: nowrap;
        }

        .apd-analyze-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .apd-close {
          flex-shrink: 0;
          font-size: 24px;
          line-height: 1;
        }

        .apd-body {
          flex: 1;
          overflow-y: auto;
          padding: 24px;
        }

        .apd-stats {
          align-items: center;
        }

        .apd-pill-paid { background: rgba(34, 197, 94, 0.2); }
        .apd-pill-unpaid { background: rgba(234, 179, 8, 0.2); }

        .apd-panes {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
          gap: 24px;
          align-items: start;
        }

        @media (max-width: 1000px) {
          .apd-panes {
            grid-template-columns: minmax(0, 1fr);
          }
          .apd-project-name {
            max-width: 70vw;
          }
        }

        .apd-pane {
          background: var(--bg-secondary, #1e293b);
          border: 1px solid var(--border-color, #334155);
          border-radius: 12px;
          padding: 20px;
          min-width: 0;
        }

        .apd-pane-title {
          margin: 0 0 16px 0;
          font-size: 15px;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: var(--text-secondary, #94a3b8);
        }

        .apd-ai-brief {
          display: flex;
          flex-direction: column;
          gap: 10px;
          padding: 14px 16px;
          margin-bottom: 16px;
          border: 1px solid rgba(139, 92, 246, 0.4);
          border-radius: 10px;
          background: rgba(139, 92, 246, 0.08);
        }

        .apd-ai-brief-title {
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: var(--ai-accent, #a78bfa);
        }

        .apd-ai-brief-text {
          font-size: 13px;
          line-height: 1.55;
          color: var(--text-primary, #f1f5f9);
          white-space: pre-wrap;
          word-break: break-word;
        }

        .apd-ai-brief-footer {
          font-size: 11px;
          color: var(--text-secondary, #94a3b8);
        }

        .apd-banner {
          padding: 10px 14px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          margin-bottom: 16px;
        }

        .apd-banner-quarantine {
          background: rgba(234, 179, 8, 0.15);
          color: var(--warning, #eab308);
          border: 1px solid rgba(234, 179, 8, 0.35);
        }

        .apd-banner-released {
          background: rgba(34, 197, 94, 0.15);
          color: var(--success, #22c55e);
          border: 1px solid rgba(34, 197, 94, 0.35);
        }

        .apd-group {
          margin-bottom: 16px;
          border: 1px solid var(--border-color, #334155);
          border-radius: 8px;
          overflow: hidden;
        }

        .apd-group-header {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 12px;
          background: var(--bg-tertiary, #334155);
          font-size: 13px;
        }

        .apd-group-code {
          font-family: monospace;
          font-weight: 600;
          letter-spacing: 0.5px;
        }

        .apd-group-subtitle {
          color: var(--text-secondary, #94a3b8);
        }

        .apd-group-count {
          margin-left: auto;
          font-size: 12px;
          color: var(--text-secondary, #94a3b8);
          white-space: nowrap;
        }

        .apd-group-body {
          display: flex;
          flex-direction: column;
        }

        .apd-doc-row,
        .apd-drawing-row,
        .apd-txn-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 10px 12px;
          border-top: 1px solid var(--border-color, #334155);
          flex-wrap: wrap;
        }

        .apd-doc-row:first-child,
        .apd-drawing-row:first-child,
        .apd-txn-row:first-child {
          border-top: none;
        }

        .apd-doc-main,
        .apd-drawing-main {
          display: flex;
          flex-direction: column;
          gap: 4px;
          min-width: 0;
          flex: 1;
        }

        .apd-doc-name {
          font-size: 13px;
          font-weight: 500;
          word-break: break-all;
        }

        .apd-doc-meta {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        .apd-chip {
          padding: 2px 8px;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 500;
          white-space: nowrap;
        }

        .apd-chip-csi {
          font-family: monospace;
          background: rgba(6, 182, 212, 0.18);
          color: var(--info, #06b6d4);
        }

        .apd-chip-filtered {
          background: rgba(37, 99, 235, 0.18);
          color: var(--primary, #60a5fa);
        }

        .apd-chip-type {
          background: rgba(100, 116, 139, 0.25);
          color: var(--text-secondary, #94a3b8);
        }

        .apd-chip-tier {
          background: rgba(100, 116, 139, 0.25);
          color: var(--text-secondary, #94a3b8);
        }

        .apd-rev-tag {
          font-size: 11px;
          color: var(--text-secondary, #94a3b8);
          opacity: 0.85;
          white-space: nowrap;
        }

        .apd-pages {
          font-size: 11px;
          color: var(--text-secondary, #94a3b8);
          white-space: nowrap;
        }

        .apd-ai-insight {
          display: flex;
          flex-direction: column;
          gap: 6px;
          padding: 8px 12px 10px 24px;
          background: rgba(139, 92, 246, 0.06);
          border-top: 1px dashed rgba(139, 92, 246, 0.3);
        }

        .apd-ai-summary {
          font-size: 12px;
          line-height: 1.45;
          color: var(--text-secondary, #94a3b8);
        }

        .apd-ai-chips {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
        }

        .apd-chip-signal {
          background: rgba(139, 92, 246, 0.16);
          color: var(--ai-accent, #a78bfa);
        }

        .apd-chip-confidence {
          background: rgba(6, 182, 212, 0.16);
          color: var(--info, #06b6d4);
        }

        .apd-ai-flags {
          display: flex;
          flex-direction: column;
          gap: 3px;
        }

        .apd-ai-flag {
          font-size: 11.5px;
          line-height: 1.4;
          color: var(--warning, #eab308);
        }

        .apd-doc-flags {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }

        .apd-parsed {
          font-size: 11px;
          white-space: nowrap;
        }

        .apd-parsed.is-parsed { color: var(--success, #22c55e); }
        .apd-parsed.not-parsed { color: var(--text-secondary, #94a3b8); }

        .apd-badge {
          padding: 3px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
          white-space: nowrap;
        }

        .apd-badge-quarantine {
          background: rgba(234, 179, 8, 0.18);
          color: var(--warning, #eab308);
        }

        .apd-badge-hot {
          background: rgba(34, 197, 94, 0.18);
          color: var(--success, #22c55e);
        }

        .apd-badge-neutral {
          background: rgba(100, 116, 139, 0.25);
          color: var(--text-secondary, #94a3b8);
        }

        .apd-subsection {
          margin-top: 24px;
        }

        .apd-subsection-title {
          margin: 0 0 12px 0;
          font-size: 13px;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: var(--text-secondary, #94a3b8);
        }

        .apd-txn-list {
          border: 1px solid var(--border-color, #334155);
          border-radius: 8px;
          overflow: hidden;
        }

        .apd-txn-main {
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0;
        }

        .apd-txn-amount {
          font-weight: 600;
          font-size: 14px;
        }

        .apd-txn-split {
          font-size: 11px;
          color: var(--text-secondary, #94a3b8);
        }

        .apd-txn-side {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }

        .apd-txn-date {
          font-size: 12px;
          color: var(--text-secondary, #94a3b8);
          white-space: nowrap;
        }

        .apd-drawing-number {
          font-family: monospace;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.5px;
        }

        .apd-drawing-title {
          font-size: 13px;
          color: var(--text-primary, #f1f5f9);
          word-break: break-word;
        }

        .apd-drawing-side {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }

        .apd-sheet {
          font-size: 11px;
          color: var(--text-secondary, #94a3b8);
          white-space: nowrap;
        }

        .apd-empty {
          padding: 24px 16px;
          text-align: center;
          color: var(--text-secondary, #94a3b8);
          font-size: 13px;
          border: 1px dashed var(--border-color, #334155);
          border-radius: 8px;
          margin-bottom: 16px;
        }

        .apd-loading {
          text-align: center;
          padding: 60px 20px;
          color: var(--text-secondary, #94a3b8);
        }

        .apd-error {
          max-width: 480px;
          margin: 60px auto;
          text-align: center;
        }

        .apd-error-message {
          padding: 14px 18px;
          margin-bottom: 16px;
          border-radius: 8px;
          background: rgba(239, 68, 68, 0.15);
          color: var(--danger, #ef4444);
          font-size: 14px;
        }

        .apd-error-actions {
          display: flex;
          gap: 8px;
          justify-content: center;
        }
      `}</style>
    </div>
  );
};

export default AdminProjectDetail;
